import { createContext, type PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { fromSlug, toSlug, type YearMonth, label as ymLabel, next as ymNext, prev as ymPrev, today as ymToday } from './models';

type CurrentMonthContextValue = {
    current: YearMonth;
    set: (ym: YearMonth) => void;
    goPrev: () => void;
    goNext: () => void;
    goToday: () => void;
    label: { month: string; year: string };
};

const CurrentMonthContext = createContext<CurrentMonthContextValue | undefined>(undefined);

const CALENDAR_PREFIX = '/calendar/';
const REPORT_PREFIX = '/report/';

const detectSection = (pathname: string): 'calendar' | 'report' =>
    pathname.startsWith(REPORT_PREFIX) || pathname === '/report' ? 'report' : 'calendar';

const slugFromPath = (pathname: string): string | null => {
    if (pathname.startsWith(CALENDAR_PREFIX)) {
        return pathname.slice(CALENDAR_PREFIX.length).split('/')[0] ?? null;
    }
    if (pathname.startsWith(REPORT_PREFIX)) {
        return pathname.slice(REPORT_PREFIX.length).split('/')[0] ?? null;
    }
    return null;
};

export const CurrentMonthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const current = useMemo<YearMonth>(() => {
        const slug = slugFromPath(location.pathname);
        if (slug) {
            const parsed = fromSlug(slug);
            if (parsed) {
                return parsed;
            }
        }
        return ymToday();
    }, [location.pathname]);

    const section = useMemo(() => detectSection(location.pathname), [location.pathname]);

    const goTo = useCallback((ym: YearMonth) => navigate(`/${section}/${toSlug(ym)}`), [section, navigate]);

    const goPrev = useCallback(() => goTo(ymPrev(current)), [current, goTo]);
    const goNext = useCallback(() => goTo(ymNext(current)), [current, goTo]);
    const goToday = useCallback(() => goTo(ymToday()), [goTo]);
    const label = useMemo(() => ymLabel(current), [current]);

    const value = useMemo<CurrentMonthContextValue>(
        () => ({ current, set: goTo, goPrev, goNext, goToday, label }),
        [current, goTo, goPrev, goNext, goToday, label],
    );

    return <CurrentMonthContext.Provider value={value}>{children}</CurrentMonthContext.Provider>;
};

export const useCurrentMonth = (): CurrentMonthContextValue => {
    const ctx = useContext(CurrentMonthContext);
    if (!ctx) {
        throw new Error('useCurrentMonth must be used within a CurrentMonthProvider');
    }
    return ctx;
};
